/* =====================================================================
 * pluginOAuthBridge — popup/tab callback coordination
 * ===================================================================== */

import { activeTenantId, activeTenantKey } from '@auw/tenant';

/* Tenant-scoped so two tenants served from the same developer origin
 * cannot cross-talk on the OAuth popup BroadcastChannel or share the
 * same `window.open` name.
 *
 * Resolved lazily so this module body can evaluate before the host
 * has registered its active tenant manifest. Vite/Rollup chunk-split
 * heuristics may co-locate this module with a chunk that evaluates
 * earlier than the host bootstrap; reading the manifest at
 * module-eval time used to throw under that condition. */
let _pluginOAuthChannel: string | null = null;
let _pluginOAuthPopupName: string | null = null;
let _pluginOAuthResultStorageKey: string | null = null;
let _pluginOAuthPendingStorageKey: string | null = null;

export function getPluginOAuthChannel(): string {
  if (_pluginOAuthChannel === null) {
    _pluginOAuthChannel = `${activeTenantId()}-plugin-oauth`;
  }
  return _pluginOAuthChannel;
}

export function getPluginOAuthPopupName(): string {
  if (_pluginOAuthPopupName === null) {
    _pluginOAuthPopupName = activeTenantKey('oauth_popup');
  }
  return _pluginOAuthPopupName;
}

function getPluginOAuthResultStorageKey(): string {
  if (_pluginOAuthResultStorageKey === null) {
    _pluginOAuthResultStorageKey = activeTenantKey('plugin_oauth_result');
  }
  return _pluginOAuthResultStorageKey;
}

function getPluginOAuthPendingStorageKey(): string {
  if (_pluginOAuthPendingStorageKey === null) {
    _pluginOAuthPendingStorageKey = activeTenantKey('plugin_oauth_pending');
  }
  return _pluginOAuthPendingStorageKey;
}

const PLUGIN_OAUTH_FLOW_PARAM = 'oauth_flow_id';
const PLUGIN_OAUTH_POPUP_PARAM = 'oauth_popup';
const OAUTH_RESULT_MAX_AGE_MS = 10 * 60 * 1000;
const OAUTH_PENDING_MAX_AGE_MS = 10 * 60 * 1000;
const CALLBACK_PARAMS_TO_CLEAR = [
  'plugin',
  'status',
  'error',
  'team_name',
  PLUGIN_OAUTH_FLOW_PARAM,
  PLUGIN_OAUTH_POPUP_PARAM,
];

export interface PluginOAuthResultMessage {
  plugin: string;
  status: 'connected' | 'error';
  error?: string;
  flowId?: string;
  popupExpected: boolean;
  issuedAt: number;
}

export interface PendingPluginOAuthFlow {
  plugin: string;
  flowId: string;
  popupExpected: boolean;
  startedAt: number;
}

function canUseLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function createNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isFreshTimestamp(value: unknown, maxAgeMs: number): value is number {
  return Number.isFinite(value) && Date.now() - Number(value) <= maxAgeMs;
}

function parseJsonValue<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function normalizeResult(raw: unknown): PluginOAuthResultMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Partial<PluginOAuthResultMessage>;
  const plugin = typeof candidate.plugin === 'string' ? candidate.plugin.trim() : '';
  const status = candidate.status;
  const issuedAt = Number(candidate.issuedAt);
  if (!plugin) return null;
  if (status !== 'connected' && status !== 'error') return null;
  if (!isFreshTimestamp(issuedAt, OAUTH_RESULT_MAX_AGE_MS)) return null;

  const flowId = typeof candidate.flowId === 'string' && candidate.flowId.trim()
    ? candidate.flowId.trim()
    : undefined;
  const error = typeof candidate.error === 'string' && candidate.error.trim()
    ? candidate.error.trim()
    : undefined;

  return {
    plugin,
    status,
    error,
    flowId,
    popupExpected: Boolean(candidate.popupExpected),
    issuedAt,
  };
}

function normalizePending(raw: unknown): PendingPluginOAuthFlow | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Partial<PendingPluginOAuthFlow>;
  const plugin = typeof candidate.plugin === 'string' ? candidate.plugin.trim() : '';
  const flowId = typeof candidate.flowId === 'string' ? candidate.flowId.trim() : '';
  const startedAt = Number(candidate.startedAt);
  if (!plugin || !flowId) return null;
  if (!isFreshTimestamp(startedAt, OAUTH_PENDING_MAX_AGE_MS)) return null;

  return {
    plugin,
    flowId,
    popupExpected: Boolean(candidate.popupExpected),
    startedAt,
  };
}

export function createPluginOAuthFlowId(): string {
  return createNonce();
}

export function buildPluginOAuthRedirectUri(
  baseUrl: string,
  params: { flowId: string; popupExpected: boolean },
): string {
  const url = new URL(
    baseUrl,
    typeof window !== 'undefined' ? window.location.origin : undefined,
  );
  url.searchParams.set(PLUGIN_OAUTH_FLOW_PARAM, params.flowId);
  url.searchParams.set(PLUGIN_OAUTH_POPUP_PARAM, params.popupExpected ? '1' : '0');
  return url.toString();
}

export function readPluginOAuthCallbackFromLocation(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): PluginOAuthResultMessage | null {
  const params = new URLSearchParams(search);
  const plugin = params.get('plugin');
  const rawStatus = params.get('status');
  const status =
    rawStatus === 'connected' || rawStatus === 'error' ? rawStatus : null;
  if (!plugin || !status) return null;

  return {
    plugin,
    status,
    error: params.get('error') || undefined,
    flowId: params.get(PLUGIN_OAUTH_FLOW_PARAM) || undefined,
    popupExpected: params.get(PLUGIN_OAUTH_POPUP_PARAM) === '1',
    issuedAt: Date.now(),
  };
}

export function publishPluginOAuthResult(
  message: Omit<PluginOAuthResultMessage, 'issuedAt'> & { issuedAt?: number },
): PluginOAuthResultMessage {
  const normalized: PluginOAuthResultMessage = {
    ...message,
    issuedAt: message.issuedAt ?? Date.now(),
  };

  try {
    const ch = new BroadcastChannel(getPluginOAuthChannel());
    ch.postMessage(normalized);
    ch.close();
  } catch {
    // BroadcastChannel 미지원 브라우저에서는 storage fallback만 사용
  }

  if (canUseLocalStorage()) {
    try {
      window.localStorage.setItem(
        getPluginOAuthResultStorageKey(),
        JSON.stringify(normalized),
      );
    } catch {
      // no-op
    }
  }

  return normalized;
}

export function readLatestPluginOAuthResult(): PluginOAuthResultMessage | null {
  if (!canUseLocalStorage()) return null;
  const storageKey = getPluginOAuthResultStorageKey();
  const parsed = parseJsonValue<PluginOAuthResultMessage>(
    window.localStorage.getItem(storageKey),
  );
  const normalized = normalizeResult(parsed);
  if (normalized) return normalized;
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // no-op
  }
  return null;
}

export function parsePluginOAuthStorageEvent(
  event: StorageEvent,
): PluginOAuthResultMessage | null {
  if (event.storageArea !== window.localStorage) return null;
  if (event.key !== getPluginOAuthResultStorageKey()) return null;
  return normalizeResult(parseJsonValue<PluginOAuthResultMessage>(event.newValue));
}

export function setPendingPluginOAuthFlow(flow: PendingPluginOAuthFlow): void {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.setItem(
      getPluginOAuthPendingStorageKey(),
      JSON.stringify(flow),
    );
  } catch {
    // no-op
  }
}

export function getPendingPluginOAuthFlow(): PendingPluginOAuthFlow | null {
  if (!canUseSessionStorage()) return null;
  const parsed = parseJsonValue<PendingPluginOAuthFlow>(
    window.sessionStorage.getItem(getPluginOAuthPendingStorageKey()),
  );
  const normalized = normalizePending(parsed);
  if (normalized) return normalized;
  try {
    window.sessionStorage.removeItem(getPluginOAuthPendingStorageKey());
  } catch {
    // no-op
  }
  return null;
}

export function hasPendingPluginOAuthFlow(flowId?: string): boolean {
  const pending = getPendingPluginOAuthFlow();
  if (!pending) return false;
  if (flowId && pending.flowId !== flowId) return false;
  return true;
}

export function clearPendingPluginOAuthFlow(flowId?: string): void {
  if (!canUseSessionStorage()) return;
  const pending = getPendingPluginOAuthFlow();
  if (!pending) return;
  if (flowId && pending.flowId !== flowId) return;
  try {
    window.sessionStorage.removeItem(getPluginOAuthPendingStorageKey());
  } catch {
    // no-op
  }
}

export function isLikelyPluginOAuthPopupWindow(): boolean {
  if (typeof window === 'undefined') return false;
  return window.name === getPluginOAuthPopupName() || window.opener !== null;
}

export function clearPluginOAuthCallbackParams(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of CALLBACK_PARAMS_TO_CLEAR) {
    if (!url.searchParams.has(key)) continue;
    url.searchParams.delete(key);
    changed = true;
  }
  if (!changed) return;
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', nextUrl || window.location.pathname);
}

export function getPluginOAuthResultIdentity(
  message: Pick<PluginOAuthResultMessage, 'plugin' | 'status' | 'error' | 'flowId' | 'issuedAt'>,
): string {
  return [
    message.flowId || '',
    message.plugin,
    message.status,
    message.error || '',
    String(message.issuedAt),
  ].join('|');
}
